import React, { useState, useEffect } from 'react';
import { Bell, Check, AlertCircle, Smartphone, MessageSquare, Send } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = supabaseUrl ? createClient(supabaseUrl, supabaseKey) : null;

const ADMIN_USER_ID = 'admin_001';

export default function NotificationPage() {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [deviceLabel, setDeviceLabel] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  // Contact Admin states
  const [contactTitle, setContactTitle] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [messageResult, setMessageResult] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }

    // Check if already subscribed
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then(registration => {
        registration.pushManager.getSubscription().then(sub => {
          setSubscription(sub);
        });
      });
    }
  }, []);

  const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const handleEnableNotifications = async () => {
    try {
      setIsRegistering(true);
      setMessage(null);

      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== 'granted') {
        setMessage({ type: 'error', text: 'Notification permission denied' });
        return;
      }

      if (!supabase) {
        setMessage({ type: 'error', text: 'Supabase not configured' });
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push notifications
      const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      if (!vapidPublicKey) {
        setMessage({ type: 'error', text: 'VAPID key not configured' });
        return;
      }

      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      setSubscription(sub);

      // Generate a unique user ID (you could use auth, but for demo we'll use a simple ID)
      const userId = localStorage.getItem('demo_user_id') || `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('demo_user_id', userId);

      const label = deviceLabel || `${navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop'} Device`;
      
      // Create or update demo user
      await supabase.from('demo_users').upsert({
        user_id: userId,
        device_label: label,
        user_agent: navigator.userAgent
      }, { onConflict: 'user_id' });

      // Save subscription
      await supabase.from('push_subscriptions').insert({
        user_id: userId,
        subscription: sub.toJSON(),
        device_label: label,
        is_enabled: true
      });

      setMessage({ type: 'success', text: 'Notifications enabled successfully!' });

      // Send test notification
      new Notification('Welcome! 🎉', {
        body: 'Notifications are now enabled for this PWA demo',
        icon: '/experiment-logo.png',
        badge: '/experiment-logo.png'
      });

    } catch (error) {
      console.error('Error enabling notifications:', error);
      setMessage({ type: 'error', text: `Error: ${error.message}` });
    } finally {
      setIsRegistering(false);
    }
  };

  const handleSendTestNotification = () => {
    if (Notification.permission === 'granted') {
      new Notification('Test Notification 🚀', {
        body: 'This is a local test notification. Admin can send push notifications from the admin panel.',
        icon: '/experiment-logo.png',
        badge: '/experiment-logo.png'
      });
      setMessage({ type: 'success', text: 'Test notification sent!' });
    }
  };

  const handleSendMessageToAdmin = async () => {
    if (!contactTitle.trim() || !contactMessage.trim()) {
      setMessageResult({ type: 'error', text: 'Please fill in both title and message' });
      return;
    }

    if (!supabase) {
      setMessageResult({ type: 'error', text: 'Supabase not configured' });
      return;
    }

    setIsSendingMessage(true);
    setMessageResult(null);

    try {
      const userId = localStorage.getItem('demo_user_id') || 'anonymous';
      const label = deviceLabel || `${navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop'} User`;

      // Send push notification to admin (edge function handles all logging)
      const { data: response, error } = await supabase.functions.invoke('send-notifications-demo', {
        body: {
          user_id: ADMIN_USER_ID,  // Target ONLY admin
          title: `Message from ${label}`,
          message: `${contactTitle}: ${contactMessage}`,
          type: 'user_to_admin',
          triggeredBy: userId,
          triggeredFromDevice: label,
        }
      });

      if (error) {
        console.error('Error sending to admin:', error);
        setMessageResult({ type: 'error', text: 'Failed to send message. Admin may not have notifications enabled.' });
        return;
      }

      // Check if message was actually sent to admin
      const summary = response?.summary || { sent: 0, total: 0 };
      if (summary.sent === 0) {
        setMessageResult({ 
          type: 'error', 
          text: 'Admin has not enabled notifications yet. Your message was logged but admin won\'t receive a push notification.' 
        });
      } else {
        setMessageResult({ 
          type: 'success', 
          text: 'Message sent to admin! Admin will receive a push notification.' 
        });
      }

      setContactTitle('');
      setContactMessage('');
      
      setTimeout(() => setMessageResult(null), 5000);
    } catch (error) {
      console.error('Error sending message to admin:', error);
      setMessageResult({ type: 'error', text: `Error: ${error.message}` });
    } finally {
      setIsSendingMessage(false);
    }
  };

  const isNotificationSupported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-500 rounded-3xl flex items-center justify-center mx-auto mb-4">
          <Bell className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-4xl font-black text-gray-900 mb-4">Push Notifications Demo</h1>
        <p className="text-lg text-gray-600">
          See how PWA notifications work - just like native apps
        </p>
      </div>

      {message && (
        <div className={`max-w-2xl mx-auto mb-6 p-4 rounded-xl flex items-center gap-3 ${
          message.type === 'success' ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'
        }`}>
          {message.type === 'success' ? (
            <Check className="w-6 h-6 text-green-600" />
          ) : (
            <AlertCircle className="w-6 h-6 text-red-600" />
          )}
          <p className={message.type === 'success' ? 'text-green-800 font-semibold' : 'text-red-800 font-semibold'}>
            {message.text}
          </p>
        </div>
      )}

      {!isNotificationSupported && (
        <div className="max-w-2xl mx-auto mb-8 bg-amber-50 border-2 border-amber-200 rounded-2xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-amber-900 mb-2">Browser Not Supported</h3>
              <p className="text-amber-800 text-sm">
                Your browser doesn't support push notifications. Try Chrome (Android/Desktop) or Safari (iOS 16.4+).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Status Cards */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
          <div className="text-center">
            <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
              permission === 'granted' ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              {permission === 'granted' ? (
                <Check className="w-6 h-6 text-green-600" />
              ) : (
                <Bell className="w-6 h-6 text-gray-400" />
              )}
            </div>
            <p className="text-sm text-gray-600 mb-1">Permission</p>
            <p className="font-bold text-gray-900 capitalize">{permission}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
          <div className="text-center">
            <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
              subscription ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              {subscription ? (
                <Check className="w-6 h-6 text-green-600" />
              ) : (
                <Smartphone className="w-6 h-6 text-gray-400" />
              )}
            </div>
            <p className="text-sm text-gray-600 mb-1">Subscription</p>
            <p className="font-bold text-gray-900">{subscription ? 'Active' : 'Not Active'}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full mx-auto mb-3 flex items-center justify-center">
              <span className="text-2xl">💪</span>
            </div>
            <p className="text-sm text-gray-600 mb-1">Support</p>
            <p className="font-bold text-gray-900">{isNotificationSupported ? 'Yes' : 'No'}</p>
          </div>
        </div>
      </div>

      {/* Main Action */}
      <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-8 text-white mb-8">
        <h2 className="text-2xl font-black mb-4">Enable Notifications</h2>
        <p className="text-white/90 mb-6">
          Get push notifications from this PWA demo. Admin can send notifications from the admin panel.
        </p>

        {permission !== 'granted' && (
          <>
            <input
              type="text"
              placeholder="Device name (optional)"
              value={deviceLabel}
              onChange={(e) => setDeviceLabel(e.target.value)}
              className="w-full p-3 rounded-xl mb-4 text-gray-900"
            />
            <button
              onClick={handleEnableNotifications}
              disabled={isRegistering || !isNotificationSupported}
              className="w-full bg-white text-orange-600 px-8 py-4 rounded-xl font-bold hover:bg-orange-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isRegistering ? (
                <>
                  <div className="w-5 h-5 border-2 border-orange-600 border-t-transparent rounded-full animate-spin" />
                  Enabling...
                </>
              ) : (
                <>
                  <Bell className="w-5 h-5" />
                  Enable Notifications
                </>
              )}
            </button>
          </>
        )}

        {permission === 'granted' && subscription && (
          <button
            onClick={handleSendTestNotification}
            className="w-full bg-white text-orange-600 px-8 py-4 rounded-xl font-bold hover:bg-orange-50 transition-all flex items-center justify-center gap-2"
          >
            <Bell className="w-5 h-5" />
            Send Test Notification
          </button>
        )}
      </div>

      {/* Contact Admin */}
      <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl p-8 text-white mb-8">
        <div className="flex items-center gap-3 mb-4">
          <MessageSquare className="w-8 h-8" />
          <h2 className="text-2xl font-black">Contact Admin</h2>
        </div>
        <p className="text-white/90 mb-6">
          Send a message to the admin. If admin has notifications enabled, they'll get a push notification!
        </p>

        {messageResult && (
          <div className={`mb-4 p-4 rounded-xl flex items-center gap-3 ${
            messageResult.type === 'success' ? 'bg-green-500/20 border-2 border-green-300' : 'bg-red-500/20 border-2 border-red-300'
          }`}>
            {messageResult.type === 'success' ? (
              <Check className="w-6 h-6 text-white" />
            ) : (
              <AlertCircle className="w-6 h-6 text-white" />
            )}
            <p className="text-white font-semibold">
              {messageResult.text}
            </p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-bold mb-2 text-white/90">Subject</label>
            <input
              type="text"
              value={contactTitle}
              onChange={(e) => setContactTitle(e.target.value)}
              className="w-full p-3 rounded-xl text-gray-900 font-semibold"
              placeholder="e.g., Question about PWA features"
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2 text-white/90">Message</label>
            <textarea
              value={contactMessage}
              onChange={(e) => setContactMessage(e.target.value)}
              className="w-full p-3 rounded-xl text-gray-900 h-32 resize-none"
              placeholder="Type your message here..."
            />
          </div>

          <button
            onClick={handleSendMessageToAdmin}
            disabled={isSendingMessage || !contactTitle.trim() || !contactMessage.trim()}
            className="w-full bg-white text-blue-600 px-8 py-4 rounded-xl font-bold hover:bg-blue-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 touch-feedback tap-target"
          >
            {isSendingMessage ? (
              <>
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Send Message to Admin
              </>
            )}
          </button>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 pb-safe">
        <h2 className="text-2xl font-black text-gray-900 mb-6">How PWA Notifications Work</h2>
        
        <div className="space-y-4">
          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
            <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
              1
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-1">User Grants Permission</h3>
              <p className="text-gray-600 text-sm">
                User clicks "Enable Notifications" and grants permission in browser prompt
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
            <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
              2
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-1">Browser Creates Push Subscription</h3>
              <p className="text-gray-600 text-sm">
                Browser registers with push service (Google FCM, Apple APN, etc.)
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
            <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
              3
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-1">Server Stores Subscription</h3>
              <p className="text-gray-600 text-sm">
                Your subscription is saved to the database (Supabase in this demo)
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
            <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
              4
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-1">Server Sends Notifications</h3>
              <p className="text-gray-600 text-sm">
                Admin can trigger push notifications via API, which are delivered even when app is closed
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
          <p className="text-sm text-green-800">
            <strong>💡 Pro Tip:</strong> After enabling notifications here, go to the Admin panel to send push notifications to all devices!
          </p>
        </div>
      </div>
    </div>
  );
}
